import type { Chunk, Event, FlatEvent, Level, LevelReason, SessionDetail } from "@backstory/protocol";
import { LEVELS } from "@backstory/protocol";

/**
 * Capture health reconstructed from the recorder's own control events.
 *
 * The recorder reports level changes and periodic health samples as ordinary
 * events, so everything here is derived on the client from the session's event
 * list — there is no separate health endpoint to keep in sync, and a session
 * recorded by an older SDK simply yields fewer facts rather than an error.
 */

/** A span of wall-clock time during which capture ran at one level. */
export interface CaptureSpan {
  /** Relative to the session start, in ms. */
  fromMs: number;
  toMs: number;
  level: Level;
  /** Why the recorder entered this level; absent for the opening span. */
  reason?: LevelReason;
}

export interface CaptureSample {
  atMs: number;
  level: Level;
  drainSec: number;
  memPct: number;
  queuedBytes: number;
  goodputBps: number;
  droppedBytes: number;
  spillBytes: number;
  selfJank: number;
  clockOffsetMs: number;
  online: boolean;
}

export interface CaptureSummary {
  spans: CaptureSpan[];
  samples: CaptureSample[];
  /** Milliseconds spent at each level; only levels actually entered appear. */
  msAtLevel: Partial<Record<Level, number>>;
  worst: Level;
  /** Levels the session dropped into, with why and for how long. */
  reasons: Array<{ reason: LevelReason; level: Level; ms: number; count: number }>;
  /** Peak observed values; undefined when no sample reported them. */
  peakDrainSec?: number;
  peakMemPct?: number;
  peakSelfJank?: number;
  slowestGoodputBps?: number;
  droppedBytes?: number;
  peakSpillBytes?: number;
  clockOffsetMs?: number;
  offline: boolean;
  /** True when this session predates health reporting, so absence is not health. */
  reported: boolean;
}

export const LEVEL_LABEL: Record<Level, string> = {
  HEALTHY: "Healthy",
  CONSTRAINED: "Constrained",
  DEGRADED: "Degraded",
  SURVIVAL: "Survival",
  PAUSED: "Paused",
};

/**
 * What each reduced level costs the recording, from the SDK's policy table.
 * Only levels that lose something are listed, and none of them repeat their own
 * name: this text exists to say what is missing from the replay, so a healthy
 * recording has nothing to add beyond its label.
 */
export const LEVEL_EFFECT: Record<Exclude<Level, "HEALTHY">, string> = {
  CONSTRAINED: "Fewer pointer and scroll samples, console warnings only, network metadata without bodies.",
  DEGRADED: "Minimal pointer sampling, console errors only, failed requests only, batched attribute changes.",
  SURVIVAL: "No pointer sampling, errors only, and buffered replay may be dropped to stay within memory.",
  PAUSED: "Nothing is being captured.",
};

export const REASON_LABEL: Record<LevelReason, string> = {
  drain: "upload queue could not keep up",
  memory: "recording memory pressure",
  offline: "device went offline",
  spill_full: "offline and local recovery storage full",
  rate_limited: "ingest rate limited the upload",
  self_jank: "recorder was slowing the page",
  forced: "pinned by project policy",
  recovery: "recovered",
};

const isLevel = (v: unknown): v is Level => typeof v === "string" && (LEVELS as readonly string[]).includes(v);
const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

/**
 * Fold the recorder's control events into spans, samples, and totals.
 *
 * Levels are held until the next change, so the last span always runs to the
 * end of the session: the recorder only speaks when something changes, and
 * silence means the level stuck rather than that capture stopped.
 */
export function captureSummary(session: SessionDetail, events: FlatEvent[]): CaptureSummary {
  const start = new Date(session.startedAt).getTime();
  const duration = Math.max(0, session.durationMs);
  const changes: Array<{ atMs: number; level: Level; reason?: LevelReason }> = [];
  const samples: CaptureSample[] = [];

  for (const e of events) {
    if (e.type !== "control") continue;
    const level = isLevel(e.name) ? e.name : undefined;
    if (!level) continue;
    const atMs = Math.min(duration, Math.max(0, e.ts - start));
    const p = e.props ?? {};
    if (e.subtype === "level") {
      changes.push({ atMs, level, reason: (e.value || undefined) as LevelReason | undefined });
    } else if (e.subtype === "health") {
      samples.push({
        atMs,
        level,
        drainSec: num(p["drainSec"]) ?? 0,
        memPct: num(p["memPct"]) ?? 0,
        queuedBytes: num(p["queuedBytes"]) ?? 0,
        goodputBps: num(p["goodputBps"]) ?? 0,
        droppedBytes: num(p["droppedBytes"]) ?? 0,
        spillBytes: num(p["spillBytes"]) ?? 0,
        selfJank: num(p["selfJank"]) ?? 0,
        clockOffsetMs: num(p["clockOffsetMs"]) ?? 0,
        online: p["online"] !== false,
      });
    }
  }
  changes.sort((a, b) => a.atMs - b.atMs);
  samples.sort((a, b) => a.atMs - b.atMs);

  // A session that starts degraded still opens with a level event, so the
  // opening span is only assumed healthy when nothing says otherwise.
  const spans: CaptureSpan[] = [];
  let at = 0;
  const opening = changes[0]?.atMs === 0 ? changes[0] : undefined;
  let level: Level = opening?.level ?? "HEALTHY";
  let reason = opening?.reason;
  for (const c of changes) {
    if (c.level === level) continue;
    if (c.atMs > at) spans.push({ fromMs: at, toMs: c.atMs, level, reason });
    at = c.atMs;
    level = c.level;
    reason = c.reason;
  }
  spans.push({ fromMs: at, toMs: duration, level, reason });

  const msAtLevel: Partial<Record<Level, number>> = {};
  for (const s of spans) msAtLevel[s.level] = (msAtLevel[s.level] ?? 0) + Math.max(0, s.toMs - s.fromMs);

  const byReason = new Map<string, { reason: LevelReason; level: Level; ms: number; count: number }>();
  for (const s of spans) {
    if (!s.reason || s.level === "HEALTHY") continue;
    const key = `${s.reason}:${s.level}`;
    const cur = byReason.get(key) ?? { reason: s.reason, level: s.level, ms: 0, count: 0 };
    cur.ms += Math.max(0, s.toMs - s.fromMs);
    cur.count++;
    byReason.set(key, cur);
  }

  const peak = (pick: (s: CaptureSample) => number) => (samples.length ? Math.max(...samples.map(pick)) : undefined);
  const worstFromSpans = spans.reduce<Level>((w, s) => (LEVELS.indexOf(s.level) > LEVELS.indexOf(w) ? s.level : w), "HEALTHY");

  return {
    spans,
    samples,
    msAtLevel,
    // The server's rollup is authoritative: it sees every chunk, while the
    // event list can be truncated on a very long session.
    worst: LEVELS.indexOf(session.level) > LEVELS.indexOf(worstFromSpans) ? session.level : worstFromSpans,
    reasons: [...byReason.values()].sort((a, b) => b.ms - a.ms),
    peakDrainSec: peak((s) => s.drainSec),
    peakMemPct: peak((s) => s.memPct),
    peakSelfJank: peak((s) => s.selfJank),
    slowestGoodputBps: samples.length ? Math.min(...samples.filter((s) => s.online).map((s) => s.goodputBps)) : undefined,
    // Dropped and spilled bytes are cumulative counters, so the last sample is the total.
    droppedBytes: samples.length ? samples[samples.length - 1]!.droppedBytes : undefined,
    peakSpillBytes: peak((s) => s.spillBytes),
    clockOffsetMs: samples.length ? samples[samples.length - 1]!.clockOffsetMs : undefined,
    offline: samples.some((s) => !s.online) || spans.some((s) => s.reason === "offline" || s.reason === "spill_full"),
    reported: changes.length > 0 || samples.length > 0,
  };
}

// ---------------------------------------------------------------------------
// Live
// ---------------------------------------------------------------------------

/**
 * Capture health while a session is still recording, folded from the chunks
 * the live socket already delivers.
 *
 * The recorded view can assume the whole story has arrived. A live console
 * cannot: when the recorder pauses, goes offline, or dies, chunks simply stop,
 * and a reading frozen at its last value would tell an agent that capture is
 * fine at the exact moment it is not. So `lastChunkAt` is part of the state and
 * silence gets its own verdict.
 */
export interface LiveCaptureHealth {
  level: Level;
  reason?: LevelReason;
  /** Epoch ms at which the current level began, as far as this console saw. */
  since: number;
  /** Worst level observed while attached. */
  worst: Level;
  /** Latest sample, when the recorder is new enough to send them. */
  sample?: CaptureSample;
  /** Epoch ms of the most recent chunk. Staleness is measured from here. */
  lastChunkAt: number;
  /** Bytes the recorder has abandoned this session. */
  droppedBytes: number;
  /** Declared holes in the stream, which no level alone would reveal. */
  gaps: number;
  /** True once a control event proved this recorder explains itself. */
  detailed: boolean;
}

/**
 * Longer than the recorder's own healthy sampling cadence, so a quiet page is
 * never mistaken for a dead one: a current SDK speaks at least once a minute
 * even with nothing to report.
 */
export const LIVE_STALE_MS = 90_000;

/**
 * Fold one live chunk into the running health state.
 *
 * `chunk.level` is the floor: every recorder on every platform stamps it, so
 * this works for mobile and for web SDKs that predate health reporting. Control
 * events, when present, refine it with the cause and the counters.
 */
export function applyLiveChunk(prev: LiveCaptureHealth | null, chunk: Chunk, now = Date.now()): LiveCaptureHealth {
  const next: LiveCaptureHealth = prev
    ? { ...prev, lastChunkAt: now }
    : { level: chunk.level, since: now, worst: chunk.level, lastChunkAt: now, droppedBytes: 0, gaps: 0, detailed: false };

  next.droppedBytes = Math.max(next.droppedBytes, chunk.droppedBytes || 0);
  next.gaps += chunk.gaps?.length ?? 0;

  let level: Level = chunk.level;
  let reason = next.reason;
  for (const e of chunk.events as Event[]) {
    if (e.type !== "control") continue;
    if (e.kind === "level") {
      next.detailed = true;
      level = e.level;
      reason = e.reason;
    } else if (e.kind === "health") {
      next.detailed = true;
      next.sample = { atMs: e.t, level: e.level, ...e.health };
      next.droppedBytes = Math.max(next.droppedBytes, e.health.droppedBytes);
    }
  }
  if (level !== next.level) {
    next.since = now;
    next.level = level;
    next.reason = reason;
  } else if (reason && !next.reason) {
    next.reason = reason;
  }
  if (LEVELS.indexOf(level) > LEVELS.indexOf(next.worst)) next.worst = level;
  return next;
}

/** How the live indicator should read right now, staleness included. */
export function liveCaptureStatus(
  health: LiveCaptureHealth | null,
  now = Date.now(),
  /** The relay says the recorded page holds no socket, which beats waiting out the silence window. */
  offline = false,
): { tone: "ok" | "info" | "warn" | "danger" | "neutral"; label: string; detail: string; stale: boolean } {
  if (!health) return { tone: "neutral", label: "No data", detail: "No recorder data has arrived on this console yet.", stale: true };
  if (offline) return { tone: "warn", label: "No data", detail: "The recorded page is no longer connected.", stale: true };
  const silentMs = now - health.lastChunkAt;
  if (silentMs > LIVE_STALE_MS) {
    // Deliberately not reported as a level: we do not know the level any more,
    // and guessing the last one would be the failure this state exists to avoid.
    return {
      tone: "warn",
      label: "No data",
      detail: `Nothing from the recorder for ${Math.round(silentMs / 1000)}s. The page may be closed, offline, or no longer recording.`,
      stale: true,
    };
  }
  const tone = health.level === "HEALTHY" ? "ok" : health.level === "CONSTRAINED" ? "info" : health.level === "DEGRADED" ? "warn" : "danger";
  if (health.level === "HEALTHY") return { tone, label: LEVEL_LABEL.HEALTHY, detail: "", stale: false };
  const why = health.reason ? ` — ${REASON_LABEL[health.reason]}` : "";
  return { tone, label: LEVEL_LABEL[health.level], detail: `${LEVEL_EFFECT[health.level]}${why}`, stale: false };
}

/** One-line verdict for the session header and the information panel. */
export function captureVerdict(summary: CaptureSummary, durationMs: number): string {
  if (!summary.reported) return "This recording predates capture health reporting.";
  if (summary.worst === "HEALTHY") return "Captured at full detail for the whole session.";
  const reduced = Object.entries(summary.msAtLevel)
    .filter(([l]) => l !== "HEALTHY")
    .reduce((sum, [, ms]) => sum + (ms ?? 0), 0);
  const share = durationMs > 0 ? Math.round((reduced / durationMs) * 100) : 0;
  const why = summary.reasons[0] ? ` (${REASON_LABEL[summary.reasons[0].reason]})` : "";
  return `Detail was reduced for ${share}% of this session, down to ${LEVEL_LABEL[summary.worst]}${why}.`;
}
